import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { FilterBar } from "@/components/data/FilterBar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { AssignControl } from "./AssignControl";
import { getSources, sourcesKey, type Source } from "./api";
import {
  FILTER_STATES,
  NO_FILTERS,
  OWNER_UNOWNED,
  filterSources,
  filterValues,
  fromBarChange,
  nextSearch,
  ownerOptions,
  readSourceFilters,
  stateCounts,
  toggleState,
  type SourceFilters,
} from "./filters";
import { STATE_WORD, counts, displayName, groupByOwner, rowNote, type OwnerGroup } from "./format";
import { SourceName } from "./SourceName";
import { StatusGlyph } from "./StatusGlyph";

/** Rows a group shows before "Show all" (UX S1: 3 + "N more"). */
const PREVIEW = 3;

/**
 * S1 Sources (UX §5/§6). The eye lands on the unowned claim: it is the only
 * primary action above the fold. The summary strip is deliberately quieter.
 */
export function SourcesTab({ onOpen }: { onOpen: (id: string) => void }) {
  const query = useQuery({ queryKey: sourcesKey, queryFn: getSources });
  const [showUnowned, setShowUnowned] = useState(false);
  const [filters, setFilters] = useSourceFilters();

  if (query.isPending) return <SourcesSkeleton />;
  if (query.isError) {
    return (
      <Alert>
        <AlertDescription>
          <b>We can't read the source registry right now.</b> Nothing here is confirmed healthy.{" "}
          <RetryButton onClick={() => query.refetch()} />
        </AlertDescription>
      </Alert>
    );
  }

  const { sources, health_readable: readable } = query.data.data;
  if (sources.length === 0) {
    return <p className="text-sm text-ks-ink">No sources registered yet. Add the first one.</p>;
  }
  // Everything below the filters reads the filtered rows: summary, claim, groups and "Show all" (KAL-916).
  const filtered = filterSources(sources, filters, readable);
  const c = counts(filtered, readable);
  // Error state (mock-up s1-sources-error): the claim, then the owners' groups.
  // The unowned list opens from "Assign owners" (or the Unowned filter), so the page still lists every source.
  const groups = groupByOwner(filtered).filter(
    (g) => readable || showUnowned || filters.owner === OWNER_UNOWNED || g.owner !== null,
  );
  const narrowed = filtered.length < sources.length;
  const openUnowned = () => {
    setShowUnowned(true);
    requestAnimationFrame(() => document.getElementById("kdi-group-__unowned")?.scrollIntoView({ block: "start" }));
  };

  return (
    <div className="flex flex-col gap-4" data-screen={readable ? "s1-sources" : "s1-sources-error"}>
      <SourceFilterBar sources={sources} readable={readable} filters={filters} onChange={setFilters} />

      {readable ? (
        <div className="flex flex-wrap gap-6 text-[13px] text-ks-grey-1" data-summary>
          <span>
            <b className="text-ks-ink">{c.total}</b>
            {narrowed ? ` of ${sources.length} sources` : " sources"}
          </span>
          <span><b className="text-ks-ink">{c.ok}</b> ok</span>
          <span><b className="text-ks-ink">{c.drifting}</b> drifting</span>
          <span><b className="text-ks-ink">{c.dark}</b> dark</span>
          <span><b className="text-ks-ink">{c.unknown}</b> couldn't check</span>
        </div>
      ) : (
        <Alert>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <b>We can't read source health right now.</b> Nothing here is confirmed healthy.
            </span>
            <RetryButton onClick={() => query.refetch()} />
          </AlertDescription>
        </Alert>
      )}

      {c.unowned > 0 && (
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-ks-purple-3 bg-ks-purple-4 p-4" data-claim>
          <div className="min-w-0 flex-1">
            <p className="text-[17px] font-bold text-ks-purple">
              {`${c.unowned} ${c.unowned === 1 ? "source has" : "sources have"} no owner`}
            </p>
            <p className="text-sm text-ks-grey-1">Nothing can be escalated for them, so nothing gets fixed.</p>
          </div>
          <Button onClick={openUnowned}>
            Assign owners
          </Button>
        </div>
      )}

      {filtered.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-ks-grey-4 shadow-xs">
          {groups.map((group) =>
            group.sources.length === 0 ? null : (
              <GroupBlock key={group.key} group={group} readable={readable} onOpen={onOpen} />
            ),
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-ks-grey-4 p-4" data-empty-filtered>
          <p className="flex-1 text-sm text-ks-ink">No sources match these filters.</p>
          <Button size="sm" variant="outline" data-nonmutating onClick={() => setFilters(NO_FILTERS)}>
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
}

/** Filters in the URL (`f_state`, `f_owner`), the portal's shareable-view rule (src/lib/filters.ts). */
function useSourceFilters(): [SourceFilters, (next: SourceFilters) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const set = (next: SourceFilters) =>
    void navigate({ to: ".", search: (prev: Record<string, unknown>) => nextSearch(prev, next), replace: true } as never);
  return [readSourceFilters(search), set];
}

/**
 * Status chips (multi-select, each with its count) and the Owner pill. The chips
 * are toggle buttons (`aria-pressed`); every control narrows a view, so each
 * carries `data-nonmutating` and survives a view-only decision (AGENTS.md rule 11).
 * Clear sits in the shared FilterBar and resets both.
 */
function SourceFilterBar({
  sources,
  readable,
  filters,
  onChange,
}: {
  sources: Source[];
  readable: boolean;
  filters: SourceFilters;
  onChange: (next: SourceFilters) => void;
}) {
  const perState = stateCounts(sources, filters.owner, readable);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5" data-filters>
      <div role="group" aria-label="Status" className="flex flex-wrap items-center gap-1.5">
        <span className="text-[13px] text-ks-grey-1">Status:</span>
        {FILTER_STATES.map((state) => {
          const on = filters.states.includes(state);
          return (
            <button
              key={state}
              type="button"
              data-nonmutating
              data-chip={state}
              aria-pressed={on}
              aria-label={`${STATE_WORD[state]}, ${perState[state]}`}
              onClick={() => onChange({ ...filters, states: toggleState(filters.states, state) })}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-ks-sm border px-3 text-[13px] transition-colors",
                on
                  ? "border-ks-purple-3 bg-ks-purple-4 font-semibold text-ks-purple"
                  : "border-ks-grey-4 bg-ks-surface text-ks-ink hover:bg-muted",
              )}
            >
              <StatusGlyph state={state} />
              <span className="tabular-nums text-ks-grey-1">{perState[state]}</span>
            </button>
          );
        })}
      </div>
      <FilterBar
        layout="inline"
        filters={[
          { name: "owner", label: "Owner", kind: "choice", anyLabel: "All owners", options: ownerOptions(sources, filters.owner) },
        ]}
        values={filterValues(filters)}
        clears={["state"]}
        onChange={(next) => onChange(fromBarChange(filters, next))}
      />
    </div>
  );
}

/** The rows a group lists: the unowned group previews {@link PREVIEW} until "Show all"; owner groups list in full. */
export function visibleRows(group: OwnerGroup, all: boolean): Source[] {
  return all || group.owner !== null ? group.sources : group.sources.slice(0, PREVIEW);
}

function GroupBlock({ group, readable, onOpen }: { group: OwnerGroup; readable: boolean; onOpen: (id: string) => void }) {
  const [all, setAll] = useState(false);
  const unowned = group.owner === null;
  // Only the unowned group previews (mock-up S1: 3 + "N more without an owner"); owner groups list in full.
  const shown = visibleRows(group, all);
  const hidden = group.sources.length - shown.length;

  return (
    <section id={`kdi-group-${group.key}`} data-group={unowned ? "unowned" : "owner"}>
      <div className="flex justify-between bg-ks-grey-4 px-4 py-2.5 text-[11px] font-bold tracking-wider text-ks-grey-1 uppercase">
        <span>{`${group.label} (${group.sources.length})`}</span>
        <span>{unowned ? "assign an owner" : ""}</span>
      </div>
      <ul>
        {shown.map((source) => (
          <SourceRow key={source.source_key} source={source} readable={readable} unowned={unowned} onOpen={onOpen} />
        ))}
        {hidden > 0 && (
          <li className="flex items-center gap-3 border-b border-ks-grey-4 px-4 py-3 last:border-b-0">
            <span className="flex-1 text-sm text-ks-grey-1">
              {`${hidden} more without an owner`}
            </span>
            <Button size="sm" variant="outline" data-nonmutating onClick={() => setAll(true)}>
              Show all
            </Button>
          </li>
        )}
      </ul>
    </section>
  );
}

function SourceRow({
  source,
  readable,
  unowned,
  onOpen,
}: {
  source: Source;
  readable: boolean;
  unowned: boolean;
  onOpen: (id: string) => void;
}) {
  const state = readable ? source.health.state : "unknown";
  return (
    <li className="flex flex-wrap items-center gap-3 border-b border-ks-grey-4 px-4 py-3 last:border-b-0" data-source={source.source_key}>
      <StatusGlyph state={state} />
      <button
        type="button"
        data-nonmutating
        className="flex min-w-0 flex-1 text-left text-sm text-ks-purple hover:underline"
        onClick={() => onOpen(source.source_id)}
      >
        <SourceName name={displayName(source)} />
      </button>
      <span className="max-w-[300px] flex-[0_1_300px] truncate text-[13px] text-ks-grey-1">{rowNote(source, readable)}</span>
      {unowned ? (
        <AssignControl sourceKey={source.source_key} />
      ) : (
        <Button size="sm" variant="outline" data-nonmutating onClick={() => onOpen(source.source_id)}>
          Open
        </Button>
      )}
    </li>
  );
}

/**
 * "Try again" inside an inverted alert (AGENTS.md rule 14). The alert's light text
 * is inherited, so an outline button on a white fill loses its label; the ink
 * colour is set on the button itself.
 */
function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" variant="outline" className="shrink-0 text-ks-ink hover:text-ks-ink" data-nonmutating onClick={onClick}>
      Try again
    </Button>
  );
}

function SourcesSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy>
      <Skeleton className="h-5 w-96" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

export function useOpenSource() {
  const navigate = useNavigate();
  return (id: string | null) =>
    navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, source: id ?? undefined }) } as never);
}
